import { Dialog, DialogActions, DialogContent, TextField } from '@mui/material';
import { MprcButton, MprcContainer, MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { AutoCompleteDataModel, ChannelAutoCompleteDataModel } from 'components/MprcAutoComplete/AutoCompleteDataModel';
import { MprcNotice } from 'components/MprcNotice';
import { MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { ShareTrainingTarget } from 'generated/CoreApi_v1';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelAutoComplete } from 'views/knowledge/channels/components/ChannelAutoComplete';
import { UserListsAutoComplete } from 'views/knowledge/publication/components/UserListsAutoComplete';

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    trainingId: string;
    trainingName: string;
    shareOption: ShareTrainingTarget;
    onClose: () => void;
}

////////////////////////////////////////////////////////////////////////////////

export const TrainingShareDialog: React.FC<IProps> = (props) => {
    const { trainingId, trainingName, shareOption, onClose } = props;

    const { t } = useTranslation("lms");
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [pubTitle, setPubTitle] = useState<string>("Treinamento: " + trainingName);
    const [text, setText] = useState<string>("");
    const [viewError, setViewError] = useState<any>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<AutoCompleteDataModel>();

    const canSave = selectedItem != undefined && pubTitle.length > 0;

    ///
    const handleSave = () => {

        if (!canSave) return;

        setSaving(true);
        api.v1.lmsStudent.shareTraining({
            trainingId,
            title: pubTitle,
            target: shareOption,
            targetId: selectedItem!.key,
            comments: text
        })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Training.Share.SavedSuccessMessage"), { variant: "success" });
                    onClose();
                } else {
                    enqueueSnackbar(t("Training.Share.SavedFailMessage"), { variant: "error" });
                }
            })
            .catch(e => {
                const r = MprcResolveRequestErrors(e);
                if (r.fieldsWithError != undefined && (r.fieldsWithError["trainingId"] || r.fieldsWithError["memberIds"])) {
                    const errors = (r.fieldsWithError["trainingId"] ?? []).concat(r.fieldsWithError["memberIds"] ?? []);
                    errors.forEach(item => enqueueSnackbar(item, { variant: "error" }));
                }
                else {
                    setViewError(e);
                }
            }).finally(() => {
                setSaving(false);
            });
    };

    return (
        <Dialog
            open
            onClose={() => onClose()}
            maxWidth="xs"
            fullWidth
        >
            <MprcDialogTitleClose
                title={shareOption == ShareTrainingTarget.Channel ? t("Training.Share.ShareInChannelTitle") : t("Training.Share.ShareInListTitle")}
                subtitle={trainingName}
                onClose={() => onClose()}
                shrinkPadding
            />
            <DialogContent>
                <MprcRequestIndicator type="post" visible={saving} />
                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

                <MprcNotice
                    items={
                        [
                            { text: shareOption == ShareTrainingTarget.Channel ? t("Training.Share.NoticeChannelText") : t("Training.Share.NoticeListText") }
                        ]
                    }
                />

                <MprcContainer>
                    {
                        shareOption == ShareTrainingTarget.Channel
                            ? <ChannelAutoComplete
                                autoFocus
                                selectedItems={selectedItem != undefined ? [selectedItem as ChannelAutoCompleteDataModel] : []}
                                onChange={items => items.length > 0 ? setSelectedItem(items[0]) : setSelectedItem(undefined)}
                                multiSelect={false}
                                placeholder={t("Training.Share.ChannelPlaceholder")}
                                label={t("Training.Share.ChannelLabel")}
                                required
                            />
                            :
                            <UserListsAutoComplete
                                autoFocus
                                selectedItems={selectedItem != undefined ? [selectedItem] : []}
                                onChange={items => items.length > 0 ? setSelectedItem(items[0]) : setSelectedItem(undefined)}
                                multiSelect={false}
                                placeholder={t("Training.Share.ListPlaceholder")}
                                label={t("Training.Share.ListLabel")}
                                required
                            />
                    }
                </MprcContainer>

                <TextField
                    fullWidth
                    required
                    value={pubTitle}
                    label={t("Training.Share.PublicationTitleLabel")}
                    onChange={(e) => setPubTitle(e.target.value)}
                    error={pubTitle.length == 0}
                    margin="normal"
                />

                <TextField
                    value={text}
                    placeholder={t("Training.Share.CommentPlaceholder")}
                    multiline={true}
                    rows={10}
                    onChange={(e) => setText(e.target.value)}
                    fullWidth
                    variant="outlined"
                />

            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", alignItems: "center" }}>
                <MprcButton
                    text={t("Training.Share.SaveButtonText")}
                    variant="contained"
                    color="primary"
                    disabled={!canSave}
                    onClick={handleSave}
                />
            </DialogActions>
        </Dialog>
    );
};
